import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import TerritoryUnitSelect from "../../components/common/TerritoryUnitSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";

const UNASSIGNED = "__none__";
const EMPTY_FORM = {
  name: "",
  specialization: "",
  qualification: "",
  clinic_hospital_name: "",
  address: "",
  territory_unit: "",
  city: "",
  pin_code: "",
  mobile: "",
  category: "B",
  monthly_visit_target: "",
  assigned_to: "",
  assigned_manager_id: "",
};

export default function DoctorFormPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY_FORM);
  const [sos, setSos] = useState([]);
  const [managers, setManagers] = useState([]);
  // The FULL roster, kept because the two pickers below are filtered and a
  // record may hold an owner the filter excludes — see `withCurrent`.
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Redirect read-only roles away from the form.
  useEffect(() => {
    if (user && user.role === "sales_officer") {
      navigate("/doctors", { replace: true });
    }
  }, [user, navigate]);

  // Specializations come from the backend, never a frontend copy. A hardcoded
  // list here is what #78 was, and `/doctors/meta/options` serves the SAME
  // `SPECIALIZATIONS` that `SpecializationType` validates against (they are one
  // object — see routes_doctors). Fetching it is what makes the dropdown and
  // the validator incapable of disagreeing.
  const [specializations, setSpecializations] = useState([]);
  useEffect(() => {
    api.get("/doctors/meta/options")
      .then(({ data }) => setSpecializations(data.specializations || []))
      .catch(() => {}); // the picker shows nothing rather than a stale guess
  }, []);

  // Load SOs for assignment.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setRoster(data);
        const filtered = data.filter((u) => u.role === "sales_officer" && u.is_active);
        // For managers, filter to team only; for admin/director, show all SOs.
        const forManager = user?.role === "assistant_manager" || user?.role === "admin_manager";
        setSos(forManager ? filtered.filter((u) => u.manager_id === user.id) : filtered);
        // Sales Managers can own a doctor outright or jointly with an SO. Not
        // team-filtered: an IC manager has no team, and a manager may pick up a
        // vacant or shared territory.
        setManagers(data.filter((u) => u.role === "assistant_manager" && u.is_active));
      } catch (_) {
        /* non-fatal */
      }
    })();
  }, [user]);

  // Load doctor for edit mode.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/doctors/${id}`);
        if (cancelled) return;
        setForm({
          ...EMPTY_FORM,
          ...data,
          // D12: the served `monthly_visit_target` may be the INHERITED
          // company figure, not this doctor's own. Editing a phone number must
          // not write the Director's number onto the row, so the form holds
          // what the RECORD stores and the inherited value is shown beside it.
          monthly_visit_target:
            data.monthly_visit_target_stored === null ||
            data.monthly_visit_target_stored === undefined
              ? ""
              : data.monthly_visit_target_stored,
          monthly_visit_target_source: data.monthly_visit_target_source,
          monthly_visit_target_inherited:
            data.monthly_visit_target_source === "setting"
              ? data.monthly_visit_target
              : null,
          mobile: data.mobile || "",
          assigned_to: data.assigned_to || "",
          assigned_manager_id: data.assigned_manager_id || "",
        });
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  /**
   * The filtered options, plus whoever this record actually holds. F312.
   *
   * The Sales Officer picker shows the viewer's own team, so a doctor owned by
   * another manager's officer rendered a BLANK owner field. The payload kept
   * the id — a blind save was safe — but the screen said "unassigned" to the
   * person doing the editing, and the next thing they do is assign somebody.
   * That is a silent unassignment performed by a human through a UI that looked
   * correct, which is worse than the one a test can catch.
   *
   * Same rule as `TerritoryUnitSelect`: a record displays what it holds, and
   * says when that value is outside the list it is being offered.
   */
  function withCurrent(options, currentId) {
    if (!currentId || options.some((o) => o.id === currentId)) return options;
    const held = roster.find((u) => u.id === currentId);
    return [
      {
        id: currentId,
        full_name: held ? held.full_name : "Current owner",
        outside: true,
        gone: !held,
      },
      ...options,
    ];
  }

  /** The out-of-list owner, named beside the picker rather than only inside
   *  it: a Radix item renders its text only once the popover has opened, so an
   *  option alone would still leave the field looking empty. */
  function CurrentOwnerNote({ currentId, options }) {
    if (!currentId || options.some((o) => o.id === currentId)) return null;
    const held = roster.find((u) => u.id === currentId);
    return (
      <p className="text-[11px] text-amber-700 font-semibold mt-1"
         data-testid={`current-owner-${currentId}`}>
        Currently {held ? held.full_name : "an account no longer in the roster"}
        {held ? " — outside your team, so not in the list above" : ""}. Leave the
        picker alone to keep them.
      </p>
    );
  }

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function validate() {
    const required = [
      "name",
      "specialization",
      "qualification",
      "clinic_hospital_name",
      "address",
      "territory_unit",
      "city",
      "pin_code",
      "category",
    ];
    for (const f of required) {
      if (!String(form[f] || "").trim()) {
        return `Please fill in ${f.replaceAll("_", " ")}`;
      }
    }
    if (!/^\d{4,12}$/.test(String(form.pin_code).trim())) {
      return "Pin code must be 4–12 digits.";
    }
    if (form.mobile && !/^[0-9+\-\s()]{7,20}$/.test(form.mobile.trim())) {
      return "Mobile number looks invalid.";
    }
    // Blank is legitimate — it means "inherit the company rule for this
    // category" (D12). Only a value that is present must be a sane number.
    if (String(form.monthly_visit_target).trim() !== "") {
      const t = Number(form.monthly_visit_target);
      if (!Number.isFinite(t) || t < 1 || t > 31) {
        return "Monthly visit target must be between 1 and 31, or left blank "
             + "to use the company rule for this category.";
      }
    }
    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        specialization: form.specialization,
        qualification: form.qualification.trim(),
        clinic_hospital_name: form.clinic_hospital_name.trim(),
        address: form.address.trim(),
        territory_unit: form.territory_unit,
        city: form.city.trim(),
        pin_code: form.pin_code.trim(),
        mobile: form.mobile.trim() || null,
        category: form.category,
        // "" means the record carries no target of its own and should inherit.
        // `Number("")` is 0, and 0 on this field says "do not visit".
        monthly_visit_target:
          String(form.monthly_visit_target).trim() === ""
            ? null
            : Number(form.monthly_visit_target),
        assigned_to: form.assigned_to && form.assigned_to !== UNASSIGNED ? form.assigned_to : null,
        assigned_manager_id:
          form.assigned_manager_id && form.assigned_manager_id !== UNASSIGNED
            ? form.assigned_manager_id
            : null,
      };
      const { data } = isEdit
        ? await api.put(`/doctors/${id}`, payload)
        : await api.post("/doctors", payload);
      if (isPendingMasterChange(data)) {
        // Filed, not applied — the change lands when its approver signs it.
        toast.success(sentForApprovalMessage(data));
        navigate(isEdit ? `/doctors/${id}` : "/doctors");
      } else {
        toast.success(isEdit ? "Doctor updated" : "Doctor added");
        navigate(`/doctors/${data.id}`);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading doctor…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doctor-form-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link
            to={isEdit ? `/doctors/${id}` : "/doctors"}
            className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
          >
            <ArrowLeft size={14} />
            Back
          </Link>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-callisto-navy">
            {isEdit ? "Edit doctor" : "Add doctor"}
          </h1>
          <p className="text-sm text-callisto-muted">
            {isEdit
              ? "Update the doctor's profile and assignment."
              : "Create a new entry in the doctor master."}
          </p>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-6"
      >
        {/* Identity */}
        <Section title="Identity">
          <Field id="name" label="Full name" required>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Dr. Full Name"
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-name"
            />
          </Field>
          <Field id="qualification" label="Qualification" required>
            <Input
              id="qualification"
              value={form.qualification}
              onChange={(e) => update("qualification", e.target.value)}
              placeholder="MBBS, MD"
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-qualification"
            />
          </Field>
          <Field id="specialization" label="Specialization" required>
            <Select
              value={form.specialization}
              onValueChange={(v) => update("specialization", v)}
            >
              <SelectTrigger
                id="specialization"
                className="h-11 focus:ring-callisto-gold"
                data-testid="doctor-input-specialization"
              >
                <SelectValue placeholder="Select specialization" />
              </SelectTrigger>
              <SelectContent>
                {specializations.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </Section>

        {/* Clinic */}
        <Section title="Clinic / hospital">
          <Field id="clinic" label="Clinic / hospital name" required colSpan={2}>
            <Input
              id="clinic"
              value={form.clinic_hospital_name}
              onChange={(e) => update("clinic_hospital_name", e.target.value)}
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-clinic"
            />
          </Field>
          <Field id="address" label="Address" required colSpan={2}>
            <Textarea
              id="address"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              rows={2}
              className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-address"
            />
          </Field>
          <Field id="territory_unit" label="Territory unit" required>
            {/* Referenced, not typed (#26): the master is the only source of
                assignable units. A flagged doctor's old string is shown as a
                hint but deliberately NOT prefilled — it is not a unit, so
                prefilling it would only fail validation on save. */}
            {form.territory_unmigrated && (form.area || form.city) && (
              <p className="mb-1.5 text-[11px] text-amber-700 font-semibold" data-testid="doctor-territory-hint">
                Needs review: was "{form.area || form.city}" — pick the unit it belongs to.
              </p>
            )}
            <TerritoryUnitSelect
              value={form.territory_unit}
              onChange={(v) => update("territory_unit", v)}
              testId="doctor-input-territory-unit"
            />
          </Field>
          <Field id="city" label="City" required>
            <Input
              id="city"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-city"
            />
          </Field>
          <Field id="pin" label="Pin code" required>
            <Input
              id="pin"
              value={form.pin_code}
              onChange={(e) => update("pin_code", e.target.value.replace(/[^0-9]/g, ""))}
              maxLength={12}
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-pin"
            />
          </Field>
          <Field id="mobile" label="Mobile (optional)">
            <Input
              id="mobile"
              value={form.mobile}
              onChange={(e) => update("mobile", e.target.value)}
              placeholder="+91 …"
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-mobile"
            />
          </Field>
        </Section>

        {/* Coverage */}
        <Section title="Coverage">
          <Field id="category" label="Category" required>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger
                id="category"
                className="h-11 focus:ring-callisto-gold"
                data-testid="doctor-input-category"
              >
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">A — High value</SelectItem>
                <SelectItem value="B">B — Medium value</SelectItem>
                <SelectItem value="C">C — Standard</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field id="target" label="Monthly visit target" required>
            <Input
              id="target"
              type="number"
              min={0}
              max={31}
              value={form.monthly_visit_target}
              placeholder={
                form.monthly_visit_target_inherited != null
                  ? `${form.monthly_visit_target_inherited} (company rule)`
                  : "Company rule"
              }
              onChange={(e) => update("monthly_visit_target", e.target.value)}
              className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-input-target"
            />
          </Field>
          <Field
            id="assignee"
            label="Assigned Sales Officer"
            hint="A doctor may have a Sales Officer, a Sales Manager, or both."
          >
            <Select
              value={form.assigned_to || UNASSIGNED}
              onValueChange={(v) => update("assigned_to", v === UNASSIGNED ? "" : v)}
            >
              <SelectTrigger
                id="assignee"
                className="h-11 focus:ring-callisto-gold"
                data-testid="doctor-input-assignee"
              >
                <SelectValue placeholder="Select sales officer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {withCurrent(sos, form.assigned_to).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.full_name}
                    {s.outside && (
                      <span className="text-amber-700 text-[11px] ml-1">
                        {s.gone ? "(no longer in the roster)" : "(outside your team)"}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <CurrentOwnerNote currentId={form.assigned_to} options={sos} />
          </Field>
          <Field id="assignee-manager" label="Assigned Sales Manager">
            <Select
              value={form.assigned_manager_id || UNASSIGNED}
              onValueChange={(v) => update("assigned_manager_id", v === UNASSIGNED ? "" : v)}
            >
              <SelectTrigger
                id="assignee-manager"
                className="h-11 focus:ring-callisto-gold"
                data-testid="doctor-input-assignee-manager"
              >
                <SelectValue placeholder="Select sales manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {withCurrent(managers, form.assigned_manager_id).map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.full_name}
                    {m.outside && (
                      <span className="text-amber-700 text-[11px] ml-1">
                        {m.gone ? "(no longer in the roster)" : "(outside your team)"}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <CurrentOwnerNote
              currentId={form.assigned_manager_id}
              options={managers}
            />
          </Field>
        </Section>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEdit ? `/doctors/${id}` : "/doctors")}
            className="h-11 border-callisto"
            data-testid="doctor-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold sm:min-w-32"
            data-testid="doctor-form-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-1.5" />
                {isEdit ? "Save changes" : "Add doctor"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
        {title}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ id, label, required, colSpan = 1, hint, children }) {
  return (
    <div className={`space-y-2 ${colSpan === 2 ? "md:col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-callisto-navy font-medium">
        {label}
        {required ? <span className="text-callisto-gold ml-0.5">*</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-[11px] text-callisto-muted">{hint}</p> : null}
    </div>
  );
}
